import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Server, Send, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";

const SITE_FIELDS = [
  { k: "studio_name", l: "Nom du studio" },
  { k: "tagline", l: "Accroche" },
  { k: "email", l: "Email de contact" },
  { k: "phone", l: "Téléphone" },
  { k: "address", l: "Adresse / zone" },
  { k: "website", l: "Site web" },
];

const SMTP_FIELDS = [
  { k: "smtp_host", l: "Serveur SMTP", ph: "mail.blacklemon-studio.fr" },
  { k: "smtp_port", l: "Port", ph: "465" },
  { k: "smtp_user", l: "Identifiant (email)", ph: "contact@blacklemon-studio.fr" },
  { k: "smtp_password", l: "Mot de passe", ph: "••••••••", type: "password" },
  { k: "smtp_from_name", l: "Nom de l'expéditeur", ph: "Black Lemon Studio" },
  { k: "notify_emails", l: "Destinataires (copies, séparés par des virgules)", ph: "contact@…, blacklemonstudio13@gmail.com" },
];

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  const load = () => api.get("/admin/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { skills, smtp_configured, ...payload } = s;
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
      load();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Saisissez une adresse email de test.");
    setTesting(true);
    try {
      // save first so SMTP config is up to date
      const { skills, smtp_configured, ...payload } = s;
      await api.put("/admin/settings", payload);
      const { data } = await api.post("/admin/email/test", { to: testTo.trim() });
      toast.success(data.message || "Email de test envoyé !");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? `Échec : ${d}` : "Échec de l'envoi du test");
    } finally { setTesting(false); }
  };

  const changePassword = async () => {
    if (pwd.new_password.length < 8) return toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères.");
    if (pwd.new_password !== pwd.confirm) return toast.error("La confirmation ne correspond pas.");
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Mot de passe mis à jour !");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de changer le mot de passe");
    } finally { setPwdBusy(false); }
  };

  if (!s) return <div className="text-zinc-500">Chargement…</div>;
  const inputCls = "w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-yellow-400 focus:outline-none";

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl tracking-tight mb-1">Paramètres</h1>
      <p className="text-zinc-500 text-sm mb-8">Informations du studio, configuration email et signature.</p>

      {/* Site info */}
      <div className="bg-[#18181B] border border-white/5 rounded-2xl p-6 space-y-4">
        <h3 className="font-bold">Informations du studio</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {SITE_FIELDS.map((f) => (
            <div key={f.k}>
              <label className="block text-xs text-zinc-400 mb-1.5">{f.l}</label>
              <input data-testid={`settings-${f.k}`} value={s[f.k] || ""} onChange={(e) => setS({ ...s, [f.k]: e.target.value })} className={inputCls} />
            </div>
          ))}
        </div>
        <div>
          <label className="block text-xs text-zinc-400 mb-1.5">À propos</label>
          <textarea data-testid="settings-about" value={s.about || ""} onChange={(e) => setS({ ...s, about: e.target.value })} rows={4} className={inputCls} />
        </div>
      </div>

      {/* SMTP */}
      <div className="mt-6 bg-[#18181B] border border-white/5 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Server className="w-5 h-5 text-yellow-400" />
          <h3 className="font-bold">Serveur email (SMTP · port 465 SSL)</h3>
          <span data-testid="smtp-status" className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${s.smtp_configured ? "bg-green-400/15 text-green-400" : "bg-yellow-400/15 text-yellow-400"}`}>
            {s.smtp_configured ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {s.smtp_configured ? "Configuré" : "Non configuré"}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {SMTP_FIELDS.map((f) => (
            <div key={f.k} className={f.k === "notify_emails" ? "sm:col-span-2" : ""}>
              <label className="block text-xs text-zinc-400 mb-1.5">{f.l}</label>
              <input data-testid={`settings-${f.k}`} type={f.type || "text"} placeholder={f.ph}
                value={s[f.k] ?? ""} onChange={(e) => setS({ ...s, [f.k]: e.target.value })}
                autoComplete={f.type === "password" ? "new-password" : "off"} className={inputCls} />
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500">La signature email (logo, nom, téléphone, email, site) est générée automatiquement à partir des informations du studio ci-dessus.</p>
      </div>

      {/* Test send */}
      <div className="mt-6 bg-[#18181B] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <Send className="w-5 h-5 text-yellow-400" />
          <h3 className="font-bold">Tester l'envoi</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">Envoyez un email de test (avec la signature) à l'adresse de votre choix pour vérifier la configuration.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input data-testid="test-email-input" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="adresse@exemple.com" className={inputCls} />
          <button data-testid="test-email-send" onClick={sendTest} disabled={testing} className="shrink-0 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white font-semibold px-5 py-2.5 rounded-md disabled:opacity-60">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer un test
          </button>
        </div>
      </div>

      <button data-testid="settings-save" onClick={save} disabled={saving} className="mt-6 flex items-center gap-2 bg-yellow-400 text-black font-bold px-6 py-3 rounded-md hover:bg-yellow-300 disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer tous les paramètres
      </button>
    </div>
  );
}
